import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { PercentageString } from "@@/shared/percentage";
import type { ProductId} from "@@/shared/products/product-id";
import { productIdZodSchema } from "@@/shared/products/product-id";
import type {
    ProviderGroupId} from "@@/shared/provider-groups/provider-group-id";
import {
    providerGroupIdZodSchema,
} from "@@/shared/provider-groups/provider-group-id";
import type {
    ProviderId} from "@@/shared/providers/provider-id";
import {
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { AccountNumber} from "./account-number";
import { accountNumberZodSchema } from "./account-number";
import type {
    AccountingDataId} from "./accounting-data-id";
import {
    accountingDataIdFactory,
    accountingDataIdZodSchema,
} from "./accounting-data-id";
import type {
    AccountingDimId} from "./accounting-dim-id";
import {
    accountingDimIdFactory,
    accountingDimIdZodSchema,
} from "./accounting-dim-id";
type AccountingData = {
    readonly _id: AccountingDataId;
    readonly _type: "ACCOUNTING_DATA";
    readonly _createdAt: IsoAndUnixTimestamp;

    /** Mappningen av kontonummer tillhör: */
    readonly ownerId: ProviderId | ProviderGroupId;

    /** Mappning. Produkter till kontonummer */
    readonly productAccountMapping: Record<ProductId, AccountNumber>;

    /** Om en produkt inte mappats så bokförs den på detta kontonummer */
    readonly fallbackProductAccount: AccountNumber;

    /**
     * "Fordringskonto" should be in the 1600-1699 range its money that the provider
     * is waiting for us to pay them. Needed to keep the SIE file in balance
     */
    readonly accountReceivable: AccountNumber;

    /** Mappning. Momssatser till kontonummer */
    readonly vatAccountMapping: Record<PercentageString, AccountNumber>;

    /** Om en momssats inte mappas så bokförs den på detta kontonummer */
    readonly fallbackVatAccount: AccountNumber;
    /** Extra data för att mappa in custom dimensions */
    readonly accountingDimensions?: AccountingDimensionData[];
};

type DimensionTarget = AccountNumber | ProductId;

type AccountingDimensionData = {
    readonly _id: AccountingDimId;
    readonly _type: "SIE_DIMENSION_DATA";
    readonly targets: DimensionTarget[];
    readonly id: string;
    readonly value: string;
};

const accountingDimensionDataFactory = (
    params: Except<AccountingDimensionData, "_id" | "_type">,
): AccountingDimensionData => {
    return {
        ...params,
        _id: accountingDimIdFactory(),
        _type: "SIE_DIMENSION_DATA",
    };
};

type FactoryParams = Except<AccountingData, "_id" | "_createdAt" | "_type"> & {
    _id?: AccountingDataId;
    _createdAt?: IsoAndUnixTimestamp;
};
const accountingDataFactory = (params: FactoryParams): AccountingData => {
    return {
        ...params,
        _id: params._id ?? accountingDataIdFactory(),
        _type: "ACCOUNTING_DATA",
        _createdAt: params._createdAt ?? nowIsoAndUnix(),
    };
};

const accountingDataZodSchema: z.ZodSchema<AccountingData> = z.object({
    _id: accountingDataIdZodSchema,
    _type: z.literal("ACCOUNTING_DATA"),
    _createdAt: isoAndUnixZodSchema,
    ownerId: z.union([providerIdZodSchema, providerGroupIdZodSchema]),
    productAccountMapping: z.record(
        productIdZodSchema,
        accountNumberZodSchema,
    ) as z.ZodSchema<Record<ProductId, AccountNumber>>,
    fallbackProductAccount: accountNumberZodSchema,
    accountReceivable: accountNumberZodSchema,
    vatAccountMapping: z.record(
        z
            .string()
            .refine(value => {
                const vatRateString = value.replace("%", "");
                return !isNaN(Number(vatRateString));
            }, "vat rate must be a percentage")
            .refine(value => {
                const vatRateString = value.replace("%", "");
                const percentage = Number(vatRateString) / 100;
                return percentage >= 0 && percentage <= 1;
            }, "vat rate must be a number within 0% and 100%"),
        accountNumberZodSchema,
    ),
    fallbackVatAccount: accountNumberZodSchema,
    accountingDimensions: z
        .array(
            z.object({
                _id: accountingDimIdZodSchema,
                _type: z.literal("SIE_DIMENSION_DATA"),
                targets: z.array(
                    z.union([accountNumberZodSchema, productIdZodSchema]),
                ),
                id: z.string(),
                value: z.string(),
            }),
        )
        .optional(),
});

export {
    accountingDataFactory,
    accountingDataZodSchema,
    accountingDimensionDataFactory,
};
export type { AccountingData, DimensionTarget };
